import React, { useState, useEffect, useRef } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import axios from "axios";
import { FileHiddenInput, WarningPopUp, Image, Anchor } from "hesus-ui-kit";
import ClipLoader from "react-spinners/ClipLoader";
import { Tooltip } from "react-tooltip";
import addIcon from "@icons/ic-add-green.svg";
import deleteIcon from "@icons/ic-delete-red.svg";
import { handleApiError } from "@utils/errorHandler";
import { downloadFileFromUrl } from "@utils/fileDownload";
import ErrorBox from "../ErrorBox";
import {
  kickOffDocumentTypes,
  documentTypeColor,
  documentName,
} from "@utils/constants";
import { ColumnButton, DivToolTip } from "@utils/extra-style";

import {
  SectionContainer,
  DocumentOuter,
  FileInputLabel,
  DocumentRow,
  FileName,
  DeleteSpan,
  DocumentDropdown,
  NoDocument,
} from "./Documents.styled";
import "../../KickOffView.scss";

const Documents = (props) => {
  const {
    t,
    id,
    entity,
    documentList,
    setIsRefreshData,
    koStatus,
    isAuthorizedSalesman,
    isAuthorizedStudy,
    documentError,
  } = props;

  const [isConfirmVisible, setConfirmVisibility] = useState(false);
  const [isConfirm, setActionConfirmStatus] = useState(false);
  const [deletedId, setDeletedId] = useState(0);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef();
  const [displayDropdown, setDisplayDropdown] = useState(false);

  const documentListRef = useRef();

  const uploadDropdownToggle = () => {
    setDisplayDropdown(!displayDropdown);
  };
  useEffect(() => {
    if (displayDropdown) {
      documentListRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [displayDropdown]);

  const triggerUploadInput = (type) => {
    fileInputRef.current.setAttribute("data-type", type);
    fileInputRef.current.click();
  };

  const onPopUpCancel = () => {
    setConfirmVisibility(false);
    setActionConfirmStatus(false);
  };

  const onPopUpConfirm = () => {
    setConfirmVisibility(false);
    setActionConfirmStatus(true);
    setLoading(true);
  };

  useEffect(() => {
    if (isConfirm) {
      axios
        .delete(`/documents/${deletedId}`)
        .then(() => {
          // onRefreshData(true);
          setConfirmVisibility(false);
          setActionConfirmStatus(false);
          setLoading(false);
          setDeletedId(0);
          setIsRefreshData(true);
        })
        .catch((error) => handleApiError(error));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfirm]);

  const deleteDocument = (id) => {
    setDeletedId(id);
    setConfirmVisibility(true);
  };

  const getDocumentList = (files) => {
    let docs = [];
    for (let i = 0; i < files?.length; i++) {
      docs[i] = (
        <FileName key={i}>
          <DeleteSpan>
            {" "}
            {koStatus === 1 &&
              (isAuthorizedSalesman !== 0 || isAuthorizedStudy !== 0) && (
                <Image
                  className="deleteImage"
                  src={deleteIcon}
                  alt={t("DOCUMENT_ACTION_TOOLTIP_DELETE")}
                  onClick={() => deleteDocument(files[i].id)}
                  styles={{ width: "auto", marginTop: "-4px" }}
                />
              )}
            <ColumnButton
              border={documentTypeColor[files[i].type.id]}
              background={documentTypeColor[files[i].type.id]}
              marginRight="10px"
              marginLeft="10px"
            >
              <span>{t(documentName[files[i].type.id])}</span>
            </ColumnButton>
          </DeleteSpan>{" "}
          <Anchor
            clickAction={() =>
              downloadFileFromUrl(files[i].downloadLink, files[i].fileName)
            }
            id={`kickoff-document-tooltip-id${files[i].id}`}
            className="kickoff-view-solution__document-link"
          >
            {files[i].fileName.substring(0, 50)}{" "}
          </Anchor>
          <Tooltip
            anchorSelect={`#kickoff-document-tooltip-id${files[i].id}`}
            style={DivToolTip}
            content={files[i].fileName}
          />
        </FileName>
      );
    }
    return docs;
  };

  const onFileSelect = (e) => {
    uploadDropdownToggle();
    const dataModified = new FormData();
    if (e.target.files.length > 0) {
      dataModified.append(entity, id);
      dataModified.append(`type`, e.target.dataset.type);
      for (let i = 0; i < e.target.files.length; i++) {
        const documentObject = e.target.files[i];
        dataModified.append(`file[${i}][filename]`, documentObject.name);
        dataModified.append(`file[${i}][file]`, documentObject);
      }
      setLoading(true);
      axios
        .post(`/documents`, dataModified, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response) => {
          // setDocumentList(response?.data?.documents);
          setLoading(false);
          setIsRefreshData(true);
        })
        .catch((error) => {});
    }
  };

  return (
    <SectionContainer>
      <DocumentOuter>
        {documentError?.length > 0 && <ErrorBox errors={documentError} />}
        <div>
          <FileInputLabel>
            {t("KICK_OFF_PAGE_SITE_DOCUMENTS")}
            {parseInt(koStatus, 10) === 1 &&
              (parseInt(isAuthorizedSalesman, 10) !== 0 ||
                parseInt(isAuthorizedStudy, 10) !== 0) && (
                <>
                  <Image
                    src={addIcon}
                    alt="upload document"
                    displayDropdown={displayDropdown}
                    onClick={uploadDropdownToggle}
                    styles={{ width: "auto", marginTop: "-4px" }}
                  />
                  {loading && <ClipLoader size={15} color="#1bace4" />}
                </>
              )}
          </FileInputLabel>

          {displayDropdown && (
            <DocumentDropdown>
              <ul className="document-dropdown-list">
                {kickOffDocumentTypes.map((item) => (
                  <li
                    key={item.id}
                    className="document-upload-list-item"
                    onClick={() => triggerUploadInput(item.id)}
                  >
                    <label className="document-label">{t(item.label)}</label>
                  </li>
                ))}
              </ul>
              <FileHiddenInput
                type="file"
                id="document"
                inputClass="document-upload-input-field"
                onFileSelect={onFileSelect}
                fileInputRef={fileInputRef}
              />
            </DocumentDropdown>
          )}
        </div>

        <DocumentRow>
          {documentList !== null && getDocumentList(documentList)}
          {documentList?.length === 0 && (
            <NoDocument>
              {t("KICK_OFF_PAGE_SITE_INFORMATIONS_SITE_DOCUMENTS_NO_DOCUMENTS")}
            </NoDocument>
          )}
        </DocumentRow>
        <div ref={documentListRef}></div>
        {isConfirmVisible && (
          <WarningPopUp
            title={t("DOCUMENT_CONFIRMATION_BOX_DELETE")}
            message={t("DOCUMENT_CONFIRMATION_BOX_DELETE_CONTENT")}
            onPopUpCancel={onPopUpCancel}
            onPopUpConfirm={onPopUpConfirm}
            ValidateContent={t("YES")}
            CancelContent={t("CANCEL")}
          />
        )}
      </DocumentOuter>
    </SectionContainer>
  );
};

Documents.propTypes = {
  t: PropTypes.func.isRequired,
};

export default withTranslation()(Documents);
